import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import CMSData from "aldoo-ra/CMS/cms-data"
import { cancelSubscription, submitUnsubscribeFeedback } from "aldoo-ra/Store/api"
import PubSub from "pubsub-js"
import { formatMoney } from "aldoo-ra/Store/currency"

function formatDate(dateString) {
  if (!dateString) return null
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

const ConfirmCancellationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  subscription,
  reason,
  feedback,
  texts,
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: reasonsData = [] } = CMSData({ contentType: "UnsubscribeReasons" })
  const reasonsArray = reasonsData[0]?.reasons

  const defaultCategoryLabels = {
    price: texts?.reasonPrice,
    technical: texts?.reasonTechnical,
    product: texts?.reasonProduct,
    autobill: texts?.reasonAutobill,
    no_need: texts?.reasonNoNeed,
    other: texts?.reasonOther,
  }

  const getCategoryFromReason = (reasonId) => {
    if (Array.isArray(reasonsArray)) {
      const match = reasonsArray.find((item) => item.key === reasonId)
      if (match) return match.value
    }
    return defaultCategoryLabels[reasonId] || defaultCategoryLabels.other
  }

  const getDurationLabel = (days) => {
    if (!days) return null
    if (days <= 7) return texts?.durationWeekly
    if (days <= 31) return texts?.durationMonthly
    if (days <= 366) return texts?.durationYearly
    return null
  }

  const endDate = formatDate(subscription?.end_date)
  const durationLabel = getDurationLabel(subscription?.duration)

  const handleConfirm = async () => {
    setLoading(true)
    setError(null)
    try {
      const subscriptionId = subscription?._id
      const response = await cancelSubscription(subscriptionId)

      if (response?.result) {
        if (reason) {
          try {
            await submitUnsubscribeFeedback({
              subscription_id: subscriptionId,
              product_id: subscription?.pid || subscription?.product?.pid || subscription?.product_id,
              category: getCategoryFromReason(reason),
              user_notes: feedback || "",
            })
          } catch {
            // feedback submission failure should not block the flow
          }
        }

        setTimeout(() => PubSub.publish("account-update"), 1500)
        onConfirm()
      } else {
        setError(response?.error || texts?.errorGeneric)
      }
    } catch {
      setError(texts?.errorUnexpected)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="centered"
      className="md:max-w-md"
      closeOnEscape={!loading}
      title={texts?.confirmTitle}
    >
      <div className="flex flex-col gap-5">
        <p className="text-sm text-text_secondary mt-0 mb-2">
          {texts?.confirmSubtitle}
        </p>

        {subscription?.product?.name && (
          <div className="bg-canvas rounded-xl p-4 flex flex-col gap-1 border border-bordered">
            <span className="text-sm font-semibold text-text">
              {subscription.product.name}
            </span>
            {(durationLabel || subscription?.product?.amount) && (
              <span className="text-xs text-text_secondary">
                {[
                  durationLabel,
                  subscription?.product?.amount
                    ? formatMoney(subscription.product.amount, subscription.product.currency?.toUpperCase(), { locale: "en" })
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </div>
        )}

        <p className="text-sm text-text_secondary">
          {texts?.activeUntilPrefix}{" "}
          <span className="font-medium text-text">
            {endDate || texts?.endOfBillingPeriod}
          </span>.
        </p>

        {error && (
          <p className="text-sm text-error bg-error/10 rounded-xl px-4 py-3">{error}</p>
        )}

        <div className="flex flex-col gap-3 justify-end">
          <Button 
            variant="primary" 
            onClick={onClose} 
            disabled={loading}
            className="flex-1"
          >
            {texts?.keepSubscription}
          </Button>

          <Button 
            variant="secondary_red" 
            onClick={handleConfirm} 
            disabled={loading}
            className="flex-1"
          >
            {loading ? <Spinner className="w-4 h-4" /> : (texts?.cancelSubscription)}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmCancellationDialog
